import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { BlockPreviewSettingsService } from '../../block-preview/block-preview-settings.service';
import { BlockPreviewSource } from '../../block-preview/block-preview-source';
import { WorkspaceView } from './panel';
import { WorkspaceStateService } from './workspace-state.service';

/**
 * The block preview on the Suburbs tool's stage (#263): the side panel is too narrow for it, so an editor in the panel
 * (the template editor, the Page tab, a block inserted on a page, the region editor) shows its preview in the stage
 * view 'preview', beside the panel. Editors nest (the Page tab, then a block in it), so the sources are a stack: the
 * newest shows, and closing it goes back to the one before. The stage shows the preview while there is a source and
 * the preview is shown (BlockPreviewSettingsService.shown: Preview on, and set up on this server), and goes back to the
 * view it replaced when either stops.
 */
@Injectable()
export class StagePreviewService implements OnDestroy {
  private stack: BlockPreviewSource[] = [];
  private replaced: WorkspaceView = 'map';
  private sub: Subscription;

  constructor(
    private state: WorkspaceStateService,
    private settings: BlockPreviewSettingsService
  ) {
    this.sub = settings.changes.subscribe(() => this.sync());
  }

  /** The view the preview covers (what the stage goes back to). */
  get replacedView(): Exclude<WorkspaceView, 'preview'> {
    return this.replaced === 'preview' ? 'map' : this.replaced;
  }

  /** What the stage previews: the newest editor's source. */
  get source(): BlockPreviewSource | null {
    return this.stack[this.stack.length - 1] ?? null;
  }

  /**
   * An editor with a preview opened (`turnOn`: the person asked for it, so Preview turns on too, and a widened panel
   * makes way for it: #274).
   */
  show(source: BlockPreviewSource, turnOn = false): void {
    this.stack = [...this.stack.filter((s) => s !== source), source];
    if (turnOn && this.state.panelExpanded) this.state.widePreview = true;
    if (turnOn && !this.settings.on) this.settings.set(true);
    else this.sync();
  }

  /** That editor closed. */
  release(source: BlockPreviewSource): void {
    this.stack = this.stack.filter((s) => s !== source);
    this.sync();
  }

  /** The panel's Close: Preview off (remembered, like the toggle), and the stage goes back. */
  close(): void {
    this.settings.set(false);
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private sync(): void {
    const shown = !!this.source && this.settings.shown;
    if (shown && this.state.view !== 'preview') {
      this.replaced = this.state.view;
      this.state.view = 'preview';
    } else if (!shown && this.state.view === 'preview') {
      this.state.view = this.replaced;
      this.state.widePreview = false;
    }
    this.state.redraw();
  }
}
